// useAgent Hook
// LazySEM Frontend - AI Agent WebSocket connection & state management

import { useState, useRef, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useOrganization } from "../contexts/OrganizationContext";
import { useAuth } from "../contexts/AuthContext";
import { refreshAccessToken } from "../auth/services/tokenRefresh";
import {
  buildAgentWsUrl,
  listSessions,
  createSession,
  deleteSession,
  getSessionMessages,
  mapHistoryType,
  getSessionRun,
  stopSessionRun,
  updateSessionTitle,
  conversationTitle,
  isLegacyTitle,
} from "../api/agent";
import type {
  ChatMessage,
  ChatSession,
  AgentWSMessage,
  ProposedPlan,
  AgentRun,
} from "../api/agent";
import { navigationItems, type NavigationItem } from "./useNavigation";

let messageCounter = 0;
const nextId = () => `msg-${++messageCounter}-${Date.now()}`;

const AGENT_WS_REFRESH_SKEW_SECONDS = Number(
  import.meta.env.VITE_AGENT_WS_REFRESH_SKEW_SECONDS ?? "0",
);
const AGENT_WS_MAX_TOKEN_RETRIES = Number(
  import.meta.env.VITE_AGENT_WS_MAX_TOKEN_RETRIES ?? "1",
);

function getJwtExpirySeconds(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const parsed = JSON.parse(atob(normalized));
    return typeof parsed.exp === "number" ? parsed.exp : null;
  } catch {
    return null;
  }
}

function shouldRefreshAgentToken(token: string): boolean {
  if (!token) return true;
  const expiresAt = getJwtExpirySeconds(token);
  if (expiresAt == null) return false;
  return (
    expiresAt <= Math.floor(Date.now() / 1000) + AGENT_WS_REFRESH_SKEW_SECONDS
  );
}

export interface UseAgentReturn {
  scopeKey?: string;
  initialSessionId?: string | null;
  unsentRequest?: string | null;
  clearUnsentRequest?: () => void;
  activeRun?: AgentRun | null;
  canStop?: boolean;
  stopRun?: () => Promise<void>;
  renameSession?: (sessionId: string, title: string) => Promise<void>;
  // State
  messages: ChatMessage[];
  sessions: ChatSession[];
  currentSessionId: string | null;
  isConnected: boolean;
  isThinking: boolean;
  error: string | null;
  pendingPlan: ProposedPlan | null; // Non-null when agent is awaiting user plan approval
  // Actions
  sendMessage: (content: string) => void | Promise<boolean>;
  newSession: () => Promise<void>;
  selectSession: (sessionId: string) => Promise<void>;
  removeSession: (sessionId: string) => Promise<void>;
  clearMessages: () => void;
  approvePlan: () => void; // Approve the pending plan and start execution
  rejectPlan: () => void; // Cancel the pending plan
}

// Find the matching navigation item for a pathname
function findNavItem(pathname: string): NavigationItem | null {
  for (const item of navigationItems) {
    if (pathname.startsWith(item.path)) return item;
    if (item.children) {
      for (const child of item.children) {
        if (pathname.startsWith(child.path)) return child;
      }
    }
  }
  return null;
}

// Parse URL segments for entity IDs (e.g. /monitoring/projects/123 → { type: 'project', id: '123' })
function parseViewing(pathname: string): { type: string; id: string } | null {
  const segments = pathname.split("/").filter(Boolean);
  // Walk pairs: .../projects/123 or .../project/5 etc.
  for (let i = segments.length - 1; i >= 1; i--) {
    const maybeId = segments[i];
    const maybeType = segments[i - 1];
    // If segment looks like an ID (digits or UUID-like)
    if (/^[0-9a-f-]+$/i.test(maybeId) && maybeId.length <= 40) {
      // Singularize simple plurals
      const type = maybeType.endsWith("s") ? maybeType.slice(0, -1) : maybeType;
      return { type, id: maybeId };
    }
  }
  return null;
}

// Summarize cached query data for a given route to send as additional_context
function getPageDataSummary(
  queryClient: ReturnType<typeof useQueryClient>,
  pathname: string,
  userId?: string,
): Record<string, unknown> | null {
  const ctx: Record<string, unknown> = {};
  const scope = [userId, localStorage.getItem("current_organization_id")];

  try {
    if (pathname.startsWith("/dashboard")) {
      const kpi = queryClient.getQueryData<any>(["dashboard", "kpi"]);
      const projects = queryClient.getQueryData<any>(["dashboard", "projects"]);
      const briefing = queryClient.getQueryData<any>([
        "dashboard",
        "briefing-projects",
        ...scope,
      ]);
      const monitoring = queryClient.getQueryData<any>([
        "dashboard",
        "monitoring-projects",
        ...scope,
      ]);
      if (kpi) ctx.kpis = kpi;
      if (projects?.recentProjects)
        ctx.recent_projects = projects.recentProjects.map((p: any) => ({
          id: p.id,
          name: p.name,
          type: p.type,
          status: p.status,
        }));
      if (briefing)
        ctx.briefing_projects_count = Array.isArray(briefing)
          ? briefing.length
          : 0;
      if (monitoring)
        ctx.monitoring_projects_count = Array.isArray(monitoring)
          ? monitoring.length
          : 0;
    } else if (pathname.startsWith("/monitoring")) {
      const data = queryClient.getQueryData<any>([
        "monitoring-projects",
        1,
        20,
      ]);
      if (data?.projects)
        ctx.projects = data.projects.map((p: any) => ({
          id: p.id,
          name: p.name,
          url: p.url,
          status: p.status,
        }));
      if (data?.total != null) ctx.total_projects = data.total;
    } else if (pathname.startsWith("/analytics")) {
      const ga4 = queryClient.getQueryData<any>(["analytics-ga4-properties"]);
      const gsc = queryClient.getQueryData<any>(["analytics-gsc-sites"]);
      if (ga4?.properties)
        ctx.ga4_properties = ga4.properties.map((p: any) => ({
          id: p.property_id,
          name: p.display_name,
        }));
      if (gsc?.sites)
        ctx.gsc_sites = gsc.sites.map((s: any) => ({
          url: s.site_url,
          permission: s.permission_level,
        }));
    } else if (pathname.startsWith("/briefings")) {
      const data = queryClient.getQueryData<any>([
        "dashboard",
        "briefing-projects",
        ...scope,
      ]);
      if (data)
        ctx.briefing_projects = Array.isArray(data)
          ? data.map((p: any) => ({ id: p.id, name: p.name, status: p.status }))
          : [];
    }
  } catch {
    // ignore cache read errors
  }

  return Object.keys(ctx).length > 0 ? ctx : null;
}

// Persisted IDs and timestamps differ from optimistic/socket messages. Consume
// matching occurrences once, preferring authoritative history while retaining
// live events that have not been persisted yet (including repeated tool calls).
function mergeTranscript(
  history: ChatMessage[],
  live: ChatMessage[],
): ChatMessage[] {
  const unmatched = [...live];
  const merged = history.map((stored) => {
    let index = unmatched.findIndex((message) => message.id === stored.id);
    if (index < 0)
      index = unmatched.findIndex((message) => {
        if (message.type !== stored.type) return false;
        const left = message.metadata?.run_id,
          right = stored.metadata?.run_id;
        if ((left || right) && left !== right) return false;
        if (
          left &&
          left === right &&
          ["TOOL_CALL", "TOOL_RESULT"].includes(stored.type)
        ) {
          return (
            !message.metadata?.tool_name ||
            !stored.metadata?.tool_name ||
            message.metadata.tool_name === stored.metadata.tool_name
          );
        }
        return message.content === stored.content;
      });
    if (index < 0) return stored;
    const local = unmatched.splice(index, 1)[0];
    return { ...stored, metadata: { ...local.metadata, ...stored.metadata } };
  });
  const firstStoredTime = history[0]?.timestamp.getTime();
  const isOlderStored = (message: ChatMessage) =>
    message.persisted &&
    firstStoredTime != null &&
    message.timestamp.getTime() < firstStoredTime;
  // A latest window overlaps, rather than replaces, the older rows already read.
  return [
    ...unmatched.filter(isOlderStored),
    ...merged,
    ...unmatched.filter((message) => !isOlderStored(message)),
  ];
}

export function useAgent(): UseAgentReturn {
  const { user } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Build rich view_context at call time (reads latest cache)
  const buildViewContext = useCallback(() => {
    const navItem = findNavItem(location.pathname);
    const viewing = parseViewing(location.pathname);
    const additionalContext = getPageDataSummary(
      queryClient,
      location.pathname,
      user?.id,
    );
    return {
      current_screen:
        navItem?.label ||
        location.pathname.split("/").filter(Boolean)[0] ||
        "unknown",
      current_path: location.pathname,
      ...(viewing ? { viewing } : {}),
      ...(additionalContext ? { additional_context: additionalContext } : {}),
    };
  }, [location.pathname, queryClient, user?.id]);

  const { currentOrganization } = useOrganization();
  const scopeKey = `${user?.id || ""}:${currentOrganization?.id || ""}`;
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;
  const [initialSessionId, setInitialSessionId] = useState<string | null>(null);
  const [unsentRequests, setUnsentRequests] = useState<Record<string, string>>(
    {},
  );
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const sessionsRef = useRef(sessions);
  sessionsRef.current = sessions;
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingPlan, setPendingPlan] = useState<ProposedPlan | null>(null);
  const [activeRun, setActiveRun] = useState<AgentRun | null>(null);
  const [stopSupported, setStopSupported] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const sessionRef = useRef<string | null>(null);
  const generation = useRef(0);
  const runRef = useRef<AgentRun | null>(null);
  const busy = useRef(false);
  const awaitingAdmission = useRef<{
    previous: string | null;
    messageId: string;
  } | null>(null);
  const sendVersion = useRef(0);
  const openingAttempt = useRef<{ socket: WebSocket; fail: () => void } | null>(
    null,
  );
  const transcriptLoads = useRef(new Map<string, Promise<boolean>>());
  const recoveredOutcomes = useRef(new Set<string>());
  const approvingRun = useRef<string | null>(null);
  const retired = useRef(new Set<string>());
  const pendingTitles = useRef(new Map<string, string>());
  const titleWrites = useRef(new Set<string>());
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>();
  const pollRef = useRef<() => void>(() => {});
  const valid = (g: number, sid: string) =>
    generation.current === g && sessionRef.current === sid;
  const setBusy = (value: boolean) => {
    busy.current = value;
    setIsThinking(value);
  };
  const persistTitle = useCallback((sid: string, g: number) => {
    const title = pendingTitles.current.get(sid);
    if (!title || titleWrites.current.has(sid)) return;
    titleWrites.current.add(sid);
    void updateSessionTitle(sid, title)
      .then(() => {
        if (valid(g, sid) && pendingTitles.current.get(sid) === title)
          pendingTitles.current.delete(sid);
      })
      .catch(() => {
        /* retry after the next authoritative run read */
      })
      .finally(() => titleWrites.current.delete(sid));
  }, []);
  const reconcileTranscript = useCallback(
    (sid: string, g: number, terminalRun?: string, latest = true) => {
      const version = sendVersion.current;
      const intent = awaitingAdmission.current;
      const key = `${g}:${version}:${terminalRun || "connect"}:${latest ? "latest" : "first"}`;
      if (terminalRun && recoveredOutcomes.current.has(key))
        return Promise.resolve(true);
      const existing = transcriptLoads.current.get(key);
      if (existing) return existing;
      const current = () =>
        valid(g, sid) &&
        sendVersion.current === version &&
        awaitingAdmission.current === intent &&
        (!terminalRun || runRef.current?.run_id === terminalRun);
      const loading = getSessionMessages(sid, 100, latest)
        .then((history) => {
          if (!current()) return false;
          const stored: ChatMessage[] = history.map((message) => ({
            id: message.id,
            type: mapHistoryType(message.type),
            content: message.content,
            timestamp: new Date(message.timestamp),
            persisted: true,
            metadata: message.metadata,
          }));
          setMessages((live) =>
            current() ? mergeTranscript(stored, live) : live,
          );
          const first = history.find((message) => message.type === "user");
          if (first && !latest)
            setSessions((list) =>
              list.map((session) =>
                session.session_id === sid && isLegacyTitle(session.title)
                  ? { ...session, title: conversationTitle(first.content) }
                  : session,
              ),
            );
          // An empty/bounded response is not proof that the outcome was recovered.
          if (
            terminalRun &&
            stored.some(
              (message) =>
                message.metadata?.run_id === terminalRun &&
                ["completed", "failed", "stopped"].includes(
                  String(message.metadata?.run_status),
                ),
            )
          )
            recoveredOutcomes.current.add(key);
          return true;
        })
        .finally(() => transcriptLoads.current.delete(key));
      transcriptLoads.current.set(key, loading);
      return loading;
    },
    [],
  );
  const applyRun = useCallback(
    (run: AgentRun, g: number, sid: string) => {
      if (
        !valid(g, sid) ||
        run.session_id !== sid ||
        retired.current.has(run.run_id)
      )
        return;
      const intent = awaitingAdmission.current;
      if (intent && run.run_id === intent.previous) return;
      const current = runRef.current;
      if (
        approvingRun.current === run.run_id &&
        run.status === "awaiting_approval"
      )
        return;
      if (current?.run_id === run.run_id) {
        if (
          ["completed", "failed", "stopped"].includes(current.status) &&
          !["completed", "failed", "stopped"].includes(run.status)
        )
          return;
        if (current.status === "stopping" && run.status === "running") return;
      }
      if (run.status !== "awaiting_approval") approvingRun.current = null;
      if (current && current.run_id !== run.run_id) {
        if (Date.parse(run.started_at) < Date.parse(current.started_at)) return;
        retired.current.add(current.run_id);
      }
      if (
        current?.run_id === run.run_id &&
        Date.parse(run.updated_at) < Date.parse(current.updated_at)
      )
        return;
      if (intent?.messageId)
        setMessages((previous) =>
          previous.map((message) =>
            message.id === intent.messageId
              ? {
                  ...message,
                  metadata: { ...message.metadata, run_id: run.run_id },
                }
              : message,
          ),
        );
      awaitingAdmission.current = null;
      runRef.current = run;
      setActiveRun(run);
      setBusy(run.status === "running" || run.status === "stopping");
      setPendingPlan(
        run.status === "awaiting_approval" ? run.plan || null : null,
      );
      persistTitle(sid, g);
      if (["completed", "failed", "stopped"].includes(run.status))
        void reconcileTranscript(sid, g, run.run_id).catch(() => {});
    },
    [persistTitle, reconcileTranscript],
  );
  const poll = useCallback(
    async (sid: string, g: number) => {
      // Every request is tied to both the session generation and the local send intent.
      const intent = awaitingAdmission.current;
      const known = runRef.current?.run_id;
      try {
        const response = await getSessionRun(sid);
        if (
          !valid(g, sid) ||
          intent !== awaitingAdmission.current ||
          known !== runRef.current?.run_id
        )
          return;
        setStopSupported(response.capabilities?.stop === true);
        if (response.run) applyRun(response.run, g, sid);
      } catch {
        if (valid(g, sid)) setStopSupported(false);
      }
    },
    [applyRun],
  );
  const sendPayload = useCallback(
    async (payload: Record<string, unknown>, g: number, sid: string) => {
      const socket = wsRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) return false;
      let token = localStorage.getItem("access_token") || "";
      if (shouldRefreshAgentToken(token)) {
        if (!(await refreshAccessToken("agent_ws_send")) || !valid(g, sid))
          return false;
        token = localStorage.getItem("access_token") || "";
      }
      if (
        !valid(g, sid) ||
        socket !== wsRef.current ||
        socket.readyState !== WebSocket.OPEN
      )
        return false;
      if (!token) {
        setError("Your session expired. Please sign in again.");
        return false;
      }
      socket.send(
        JSON.stringify({
          ...payload,
          user_id: user?.id || "",
          org_id:
            currentOrganization?.id ||
            localStorage.getItem("current_organization_id") ||
            "",
          api_token: token,
          view_context: buildViewContext(),
        }),
      );
      return true;
    },
    [user?.id, currentOrganization?.id, buildViewContext],
  );
  const connectWs = useCallback(
    (sid: string, g: number, onReady?: () => void, onUnsent?: () => void) => {
      if (!user?.id || !valid(g, sid)) {
        onUnsent?.();
        return;
      }
      openingAttempt.current?.fail();
      openingAttempt.current = null;
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
      setIsConnected(false);
      const socket = new WebSocket(buildAgentWsUrl(user.id, sid));
      wsRef.current = socket;
      const live = () => valid(g, sid) && wsRef.current === socket;
      let retries = 0;
      let opened = false;
      let settled = false;
      const failBeforeOpen = () => {
        if (!opened && !settled) {
          settled = true;
          onUnsent?.();
        }
        if (openingAttempt.current?.socket === socket)
          openingAttempt.current = null;
      };
      if (onReady) openingAttempt.current = { socket, fail: failBeforeOpen };
      socket.onopen = () => {
        if (!live()) return;
        if (settled) return;
        opened = true;
        if (openingAttempt.current?.socket === socket)
          openingAttempt.current = null;
        setIsConnected(true);
        setError(null);
        void reconcileTranscript(sid, g).catch(() => {});
        void poll(sid, g);
        onReady?.();
      };
      socket.onmessage = (event) => {
        if (!live()) return;
        try {
          const msg: AgentWSMessage = JSON.parse(event.data);
          if (
            (msg.session_id || msg.metadata?.session_id) &&
            (msg.session_id || msg.metadata?.session_id) !== sid
          )
            return;
          if (msg.type === "RUN_STATUS") {
            if (msg.run) applyRun(msg.run, g, sid);
            return;
          }
          if (
            msg.run_id &&
            (retired.current.has(msg.run_id) ||
              (runRef.current && runRef.current.run_id !== msg.run_id))
          )
            return;
          if (msg.type === "TOKEN_EXPIRED") {
            if (retries++ >= AGENT_WS_MAX_TOKEN_RETRIES) {
              setError(
                "The agent request still failed authorization. Please try again.",
              );
              setBusy(false);
              return;
            }
            void refreshAccessToken("agent_ws_401").then((ok) => {
              if (ok && live())
                void sendPayload(
                  { content: msg.retry_query || msg.content },
                  g,
                  sid,
                );
            });
            return;
          }
          if (msg.type === "PLAN_PROPOSED") {
            setPendingPlan(msg.plan || null);
            setBusy(false);
            void poll(sid, g);
            return;
          }
          if (msg.type === "PLAN_REJECTED") {
            setPendingPlan(null);
            void poll(sid, g);
            return;
          }
          if (msg.type === "error" || msg.type === "ERROR") {
            approvingRun.current = null;
            setError(msg.content || "The agent request failed.");
            if (
              !runRef.current ||
              !["running", "stopping"].includes(runRef.current.status)
            )
              setBusy(false);
            void poll(sid, g);
            return;
          }
          if (msg.type === "DONE" || msg.type === "AGENT") {
            if (!runRef.current) {
              setBusy(false);
              awaitingAdmission.current = null;
            }
            void poll(sid, g);
            if (msg.type === "DONE") return;
          }
          if (
            msg.type === "THINKING" &&
            (!runRef.current || runRef.current.status === "running")
          )
            setBusy(true);
          const metadata = {
            ...msg.metadata,
            run_id:
              msg.run_id || msg.metadata?.run_id || runRef.current?.run_id,
            step:
              (msg as AgentWSMessage & { step?: number }).step ??
              msg.metadata?.step,
            tool_name: msg.tool_name || msg.metadata?.tool_name,
            tool_input:
              msg.params ||
              msg.metadata?.tool_input ||
              (msg.metadata?.params as Record<string, unknown> | undefined),
            tool_output:
              msg.result ?? msg.metadata?.tool_output ?? msg.metadata?.result,
          };
          if (
            !["THINKING", "TOOL_CALL", "TOOL_RESULT", "AGENT"].includes(
              msg.type,
            )
          )
            return;
          setMessages((prev) => {
            // The server persists its final answer before publishing AGENT.
            if (
              msg.type === "AGENT" &&
              metadata.run_id &&
              prev.some(
                (message) =>
                  message.type === "AGENT" &&
                  message.metadata?.run_id === metadata.run_id &&
                  message.content === (msg.content || ""),
              )
            )
              return prev;
            return [
              ...prev,
              {
                id: nextId(),
                type: msg.type,
                content: msg.content || "",
                timestamp: new Date(),
                metadata,
              },
            ];
          });
        } catch {
          /* ignore malformed frames */
        }
      };
      const disconnect = () => {
        failBeforeOpen();
        if (!live()) return;
        approvingRun.current = null;
        setIsConnected(false);
        reconnectTimer.current = setTimeout(() => {
          if (live()) connectWs(sid, g);
        }, 2000);
      };
      socket.onclose = disconnect;
      socket.onerror = disconnect;
    },
    [user?.id, poll, applyRun, sendPayload, reconcileTranscript],
  );
  const activate = useCallback(
    (sid: string) => {
      setInitialSessionId(null);
      const g = ++generation.current;
      openingAttempt.current?.fail();
      openingAttempt.current = null;
      sessionRef.current = sid;
      setCurrentSessionId(sid);
      setMessages([]);
      setError(null);
      setPendingPlan(null);
      runRef.current = null;
      setActiveRun(null);
      setBusy(false);
      setStopSupported(false);
      awaitingAdmission.current = null;
      retired.current.clear();
      approvingRun.current = null;
      connectWs(sid, g);
      void poll(sid, g);
      return g;
    },
    [connectWs, poll],
  );
  useEffect(() => {
    let active = true;
    ++generation.current;
    openingAttempt.current?.fail();
    openingAttempt.current = null;
    sessionRef.current = null;
    wsRef.current?.close();
    wsRef.current = null;
    clearTimeout(reconnectTimer.current);
    setInitialSessionId(null);
    setUnsentRequests({});
    setCurrentSessionId(null);
    setMessages([]);
    setSessions([]);
    setActiveRun(null);
    runRef.current = null;
    setPendingPlan(null);
    setBusy(false);
    setIsConnected(false);
    setError(null);
    setStopSupported(false);
    awaitingAdmission.current = null;
    pendingTitles.current.clear();
    if (user?.id)
      void listSessions(user.id)
        .then((res: any) => {
          if (active)
            setSessions(Array.isArray(res) ? res : res?.sessions || []);
        })
        .catch(() => {});
    return () => {
      active = false;
      ++generation.current;
      openingAttempt.current?.fail();
      openingAttempt.current = null;
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [scopeKey]); // organization context, rather than an unobserved localStorage read
  pollRef.current = () => {
    const sid = sessionRef.current;
    if (sid) void poll(sid, generation.current);
  };
  useEffect(() => {
    const timer = setInterval(() => pollRef.current(), 2000);
    return () => clearInterval(timer);
  }, []);
  const selectSession = useCallback(
    async (sid: string) => {
      const g = activate(sid);
      try {
        await reconcileTranscript(sid, g, undefined, false);
      } catch {
        if (valid(g, sid))
          setError(
            "Conversation history could not be loaded. Try opening it again.",
          );
      }
    },
    [activate, reconcileTranscript],
  );
  const newSession = useCallback(async () => {
    if (!user?.id) return;
    const g = generation.current;
    try {
      const session = await createSession(
        user.id,
        currentOrganization?.id || "",
      );
      if (g !== generation.current) return;
      setSessions((list) => [session, ...list]);
      activate(session.session_id);
    } catch (e) {
      if (g === generation.current)
        setError(e instanceof Error ? e.message : "Could not create chat");
    }
  }, [user?.id, currentOrganization?.id, activate]);
  const sendMessage = useCallback(
    async (content: string) => {
      if (
        !user?.id ||
        busy.current ||
        pendingPlan ||
        runRef.current?.status === "awaiting_approval" ||
        !content.trim()
      )
        return false;
      ++sendVersion.current;
      setBusy(true);
      setError(null);
      let sid = sessionRef.current;
      let g = generation.current;
      let createdSession = false;
      if (sid && !runRef.current) {
        // Establish the previous server run before sending; browser/server clocks may differ.
        try {
          const recovery = await getSessionRun(sid);
          if (!valid(g, sid)) return false;
          setStopSupported(recovery.capabilities?.stop === true);
          if (recovery.run) {
            applyRun(recovery.run, g, sid);
            if (
              ["running", "stopping", "awaiting_approval"].includes(
                recovery.run.status,
              )
            ) {
              setError("This conversation already has work in progress.");
              return false;
            }
          }
        } catch {
          /* Older servers still support ordinary chat. */
        }
        if (!valid(g, sid)) return false;
        setBusy(true);
      }
      if (!sid) {
        try {
          const session = await createSession(
            user.id,
            currentOrganization?.id || "",
          );
          if (g !== generation.current) return false;
          setSessions((list) => [session, ...list]);
          sid = session.session_id;
          g = activate(sid);
          createdSession = true;
          setInitialSessionId(sid);
          setBusy(true);
        } catch (e) {
          if (g === generation.current) {
            setBusy(false);
            setError("Could not create chat");
          }
          return false;
        }
      }
      const id = sid;
      const messageId = nextId();
      const intent = { previous: runRef.current?.run_id || null, messageId };
      awaitingAdmission.current = intent;
      if (runRef.current) retired.current.add(runRef.current.run_id);
      const session = sessionsRef.current.find(
        (item) => item.session_id === id,
      );
      const originalTitle = session?.title;
      const automaticTitle = conversationTitle(content);
      if (createdSession || (session && isLegacyTitle(session.title))) {
        const title = automaticTitle;
        pendingTitles.current.set(id, title);
        setSessions((list) =>
          list.map((item) =>
            item.session_id === id ? { ...item, title } : item,
          ),
        );
      }
      setMessages((prev) => [
        ...prev,
        { id: messageId, type: "user", content, timestamp: new Date() },
      ]);
      const failed = () => {
        if (!valid(g, id) || awaitingAdmission.current !== intent) return;
        setBusy(false);
        awaitingAdmission.current = null;
        if (intent.previous) retired.current.delete(intent.previous);
        setUnsentRequests((previous) => ({ ...previous, [id]: content }));
        setMessages((previous) =>
          previous.filter((message) => message.id !== messageId),
        );
        if (pendingTitles.current.get(id) === automaticTitle)
          pendingTitles.current.delete(id);
        setSessions((list) =>
          list.map((item) =>
            item.session_id === id && item.title === automaticTitle
              ? { ...item, title: originalTitle }
              : item,
          ),
        );
        setError(
          "Message was not sent. Reconnect and restore the request to try again.",
        );
      };
      const send = async () => {
        try {
          const ok = await sendPayload({ content }, g, id);
          if (!ok) failed();
          else if (valid(g, id))
            setUnsentRequests((previous) => {
              const next = { ...previous };
              delete next[id];
              return next;
            });
          return ok;
        } catch {
          failed();
          return false;
        }
      };
      if (wsRef.current?.readyState === WebSocket.OPEN) return send();
      return new Promise<boolean>((resolve) =>
        connectWs(
          id,
          g,
          () => {
            void send().then(resolve);
          },
          () => {
            failed();
            resolve(false);
          },
        ),
      );
    },
    [
      user?.id,
      currentOrganization?.id,
      pendingPlan,
      activate,
      sendPayload,
      connectWs,
      applyRun,
    ],
  );
  const stopRun = useCallback(async () => {
    const run = runRef.current,
      sid = sessionRef.current,
      g = generation.current;
    if (!run || !sid || !stopSupported) return;
    try {
      const response = await stopSessionRun(sid, run.run_id);
      if (
        valid(g, sid) &&
        runRef.current?.run_id === run.run_id &&
        response.run?.run_id === run.run_id
      )
        applyRun(response.run, g, sid);
    } catch (e) {
      if (valid(g, sid) && runRef.current?.run_id === run.run_id)
        setError(
          e instanceof Error
            ? e.message
            : "Could not stop further work. Try again.",
        );
    }
  }, [stopSupported, applyRun]);
  const renameSession = useCallback(async (sid: string, title: string) => {
    const scope = scopeRef.current;
    await updateSessionTitle(sid, title.trim());
    if (scope !== scopeRef.current) return;
    pendingTitles.current.delete(sid);
    setSessions((list) =>
      list.map((s) =>
        s.session_id === sid ? { ...s, title: title.trim() } : s,
      ),
    );
  }, []);
  const removeSession = useCallback(async (sid: string) => {
    const scope = scopeRef.current;
    await deleteSession(sid);
    if (scope !== scopeRef.current) return;
    setSessions((list) => list.filter((s) => s.session_id !== sid));
    if (sessionRef.current === sid) {
      ++generation.current;
      sessionRef.current = null;
      setCurrentSessionId(null);
      wsRef.current?.close();
      setMessages([]);
      setPendingPlan(null);
      runRef.current = null;
      setActiveRun(null);
      setBusy(false);
      setIsConnected(false);
    }
  }, []);
  const decidePlan = useCallback(
    (approved: boolean) => {
      const sid = sessionRef.current,
        g = generation.current,
        run = runRef.current;
      if (!sid || !pendingPlan) return;
      void sendPayload(
        {
          type: approved ? "plan_approved" : "plan_rejected",
          ...(run ? { run_id: run.run_id } : {}),
        },
        g,
        sid,
      ).then((ok) => {
        if (!ok || !valid(g, sid)) return;
        if (approved) {
          approvingRun.current = run?.run_id || null;
          setPendingPlan(null);
          setBusy(true);
        }
        void poll(sid, g);
      });
    },
    [pendingPlan, sendPayload, poll],
  );
  return {
    scopeKey,
    initialSessionId,
    unsentRequest:
      unsentRequests[currentSessionId || "__new_conversation__"] || null,
    clearUnsentRequest: () =>
      setUnsentRequests((previous) => {
        const next = { ...previous };
        delete next[sessionRef.current || "__new_conversation__"];
        return next;
      }),
    messages,
    sessions,
    currentSessionId,
    isConnected,
    isThinking,
    error,
    pendingPlan,
    activeRun,
    canStop: stopSupported && !!activeRun && activeRun.status === "running",
    stopRun,
    renameSession,
    sendMessage,
    newSession,
    selectSession,
    removeSession,
    clearMessages: () => setMessages([]),
    approvePlan: () => decidePlan(true),
    rejectPlan: () => decidePlan(false),
  };
}
export default useAgent;
